/**
 * No-arg API client factory plus TanStack Query helpers.
 *
 * `createApiClient()` builds the single oRPC API client. It takes no
 * arguments: call-context metadata (language / clientVersion / url) is
 * resolved from the URL on every request via `resolveRequestMetadataFromUrl()`
 * (`./request-metadata`) and serialized into standard headers. Resource
 * identity (silo / structure / instance) is NOT bound here — it travels in
 * each procedure's input, declared on the contract URL path (e.g.
 * `POST /silo/{siloKey}/spaces`).
 *
 * The real client is created once at the app root (`app/_layout.tsx`) and
 * handed to `<ApiClientProvider>`; React components read it via
 * `useApiClient()` / `useApiQueryUtils()` from `system/api-client-context`.
 * Non-React callers that already hold a `Datastore` reach the same client via
 * `datastore.getApiClient()` (a passthrough to the provided client).
 *
 * The API client is currently implemented on top of oRPC (`@orpc/client`), but
 * the consumer-facing names don't bake in that choice — calls go through
 * `api.<module>.<fn>(...)` with shapes derived from the shared contract.
 *
 * Auth travels two ways: same-origin requests carry the better-auth session
 * cookie (`credentials: 'same-origin'`); cross-origin requests get their
 * `Authorization` header set by `authClient.signRequest(headers)` — an action
 * the active auth plugin exposes that mutates outgoing headers in place. This
 * module doesn't know what auth scheme is in play (bearer, Firebase ID token,
 * future HMAC, etc.) — only that the auth client knows how to sign a request.
 * Mirrors how the legacy `authClient.$fetch` pipeline attaches auth via its
 * `onRequest` plugin hook, just without going through `$fetch` itself (RPCLink
 * wants the raw `Response`, which `$fetch` doesn't return).
 *
 * Error handling is the caller's responsibility. A failed call rejects the
 * promise; callers `catch` it, handle anything they want with contextually
 * appropriate feedback, and pass the rest to `showApiErrorToast`
 * (`system/orpc-errors.ts`) for a generic toast. The client itself adds no
 * out-of-band error UI.
 */
import { createORPCClient, ORPCError } from '@orpc/client';
import { RPCLink } from '@orpc/client/fetch';
import type { ContractRouterClient } from '@orpc/contract';
import { createRouterUtils, type RouterUtils } from '@orpc/react-query';
import type { AppContract } from '@repo/shared/orpc/contract';
import { serializeRequestMetadata } from '@repo/shared/orpc/metadata-headers';
import { QueryClient } from '@tanstack/react-query';
import { authClient } from 'util/auth-client';
import { getApiBaseUrl } from 'util/util';

import { resolveRequestMetadataFromUrl } from './request-metadata';

export type ApiClient = ContractRouterClient<AppContract>;

/**
 * Build the single API client. Takes no arguments — request metadata is
 * resolved from the URL on every outgoing call.
 */
export function createApiClient(): ApiClient {
    const link = new RPCLink({
        // The typed RPC transport is mounted alongside the REST transport
        // under /api/v2/. `getApiBaseUrl()` returns a value ending in `/api/`.
        url: () => `${getApiBaseUrl()}v2/rpc`,
        headers: () => serializeRequestMetadata(resolveRequestMetadataFromUrl()),
        fetch: async (request, init) => {
            // Let the active auth plugin (bearer / firebase / ...) attach its
            // auth header(s). RPCLink doesn't need to know what scheme is in
            // play.
            const headers = new Headers(request.headers);
            await authClient.signRequest?.(headers);
            return await fetch(new Request(request, { headers }), { ...init, credentials: 'same-origin' });
        },
    });
    return createORPCClient(link);
}

/**
 * TanStack Query helpers for a given client (use one per provided client
 * alongside the client).
 *
 * @public
 */
export function createApiQueryUtils(client: ApiClient): RouterUtils<ApiClient> {
    return createRouterUtils(client);
}

/**
 * TanStack's three attempts, except for a request the server has definitively
 * refused. A 4xx is an answer — not found, not allowed, not valid — so retrying
 * only delays the screen that has to render it, and it delays it again for each
 * fresh observer that mounts on the failed query.
 *
 * 401 is the exception, because it isn't definitive: `createApiClient`'s
 * `fetch` re-signs every attempt, and the Firebase `signRequest` mints a fresh
 * ID token when the current one has expired. So the retry goes out with a
 * different `Authorization` header than the one that was rejected, and a
 * token blip (clock skew, a revocation between mint and use) resolves itself
 * rather than becoming an error the user has to reload out of.
 */
function retryUnlessRefused(failureCount: number, error: Error): boolean {
    if (error instanceof ORPCError && error.status >= 400 && error.status < 500 && error.status !== 401) return false;
    return failureCount < 3;
}

/** @public */
export function createOrpcQueryClient(): QueryClient {
    return new QueryClient({
        defaultOptions: { queries: { staleTime: 30_000, retry: retryUnlessRefused } },
    });
}
